import { motion } from "framer-motion";
import { CheckCircle, Clock, FileText, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { businessRef, POSTAL_ADDRESS } from "@/lib/business";

export default function Pricing() {
  const packages = [
    {
      name: "Building Status Report",
      price: "From $325",
      description: "Visual and digital check for approval related matters",
      features: [
        "Visual property inspection",
        "Digital documentation with photos",
        "Approval status assessment",
        "72 hour report delivery",
        "Email delivery"
      ],
      popular: false
    },
    {
      name: "Building Inspection Report",
      price: "From $455",
      description: "Complete inspection with condition & remediation reports",
      features: [
        "Visual condition inspection",
        "Condition Report included",
        "Remediation Report included",
        "Digital report with photos",
        "Cost estimates for repairs",
        "72 hour delivery"
      ],
      popular: true
    },
    {
      name: "Strata Report",
      price: "Contact us for pricing",
      priceOnRequest: true,
      description: "Independent report for strata and unit properties, quoted to suit",
      features: [
        "Scoped to your property",
        "Independent, impartial reporting",
        "Digital report with photos",
        "Price confirmed before we start"
      ],
      popular: false
    },
    {
      name: "Pest Report",
      price: "From $325",
      description: "Comprehensive pest and termite inspection",
      features: [
        "Thorough pest inspection",
        "Termite activity assessment",
        "Digital report with photos",
        "72 hour delivery",
        "Treatment recommendations"
      ],
      popular: false
    },
    {
      name: "Pool Remediation Report",
      price: "From $260",
      description: "Pool safety compliance check",
      features: [
        "Pool barrier inspection",
        "Gate and fence assessment",
        "Compliance guidance",
        "Digital report with photos",
        "Government requirements check"
      ],
      popular: false
    },
    {
      name: "Plumbing Report",
      price: "From $520",
      description: "Full visual plumbing assessment",
      features: [
        "Visual plumbing inspection",
        "Water supply assessment",
        "Drainage system check",
        "Fixture condition review",
        "72 hour delivery"
      ],
      popular: false
    },
    {
      name: "Electrical Report",
      price: "From $585",
      description: "Full scope electrical assessment",
      features: [
        "Full scope electrical inspection",
        "Qualified inspection",
        "Insured reporting",
        "Safety compliance check",
        "Professional assessment"
      ],
      popular: false
    }
  ];

  const addOns = [
    "Pest and termite inspection via trusted partners",
    "Electrical inspection via trusted partners",
    "Additional property buildings or structures",
    "Expedited report delivery",
    "Follow up consultation"
  ];

  return (
    <div>
      <SEO
        title="Pricing & Packages"
        description="Transparent pricing for building and property inspections in Darwin NT. Flat rate packages from $260 including 72 hour report delivery."
        keywords="building inspection cost Darwin, property inspection pricing NT, inspection packages Darwin, building report prices"
        url="/pricing"
        schema={{
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          "name": "Aussie Inspections — inspection packages",
          "url": "https://aussieinspections.com.au/pricing",
          // Built from `packages` above so the published prices and the schema
          // cannot drift apart. "From $325" -> lowPrice 325.
          "itemListElement": packages.map((p, i) => ({
            "@type": "Offer",
            "position": i + 1,
            "name": p.name,
            "description": p.description,
            "priceCurrency": "AUD",
            ...(p.priceOnRequest ? {} : {
              "priceSpecification": {
                "@type": "PriceSpecification",
                "minPrice": Number(String(p.price).replace(/[^0-9.]/g, "")),
                "priceCurrency": "AUD",
                "valueAddedTaxIncluded": true
              }
            }),
            "availableAtOrFrom": {
              "@type": "Place",
              "address": POSTAL_ADDRESS
            },
            "seller": businessRef
          }))
        }}
      />

      <CTABanner />

      {/* Hero */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-chivo text-4xl md:text-5xl font-black text-slate-900 mb-6">
              Transparent Pricing
            </h1>
            <p className="text-xl text-slate-600">
              Flat rate pricing with no hidden costs. All packages include digital reports with photos delivered within 72 hours. Starting from just $260 inc. GST.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white border-2 rounded-lg p-8 flex flex-col h-full ${
                  pkg.popular
                    ? "border-primary shadow-lg"
                    : "border-slate-200"
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-bold">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-chivo text-2xl font-bold text-slate-900 mb-2 min-h-[64px]">
                    {pkg.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 min-h-[40px]">{pkg.description}</p>
                  <div className="flex items-baseline">
                    <span className="font-chivo text-4xl font-black text-primary">
                      {pkg.price}
                    </span>
                    {pkg.price.includes("$") && (
                      <span className="text-slate-500 ml-2">inc. GST</span>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  className={`w-full mt-auto ${
                    pkg.popular
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-slate-900 hover:bg-slate-800"
                  } text-white font-bold uppercase`}
                >
                  <Link to="/contact">Get Quote</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Affects Pricing */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
              What Affects Pricing?
            </h2>
            <p className="text-lg text-slate-600">
              While we offer flat rate pricing, final cost may vary based on:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: MapPin,
                title: "Property Location",
                description: "Properties in Darwin and Palmerston are standard rate. Outer areas may incur a small travel fee."
              },
              {
                icon: FileText,
                title: "Property Size",
                description: "Larger properties or those with multiple buildings may require additional inspection time."
              },
              {
                icon: Clock,
                title: "Inspection Complexity",
                description: "Properties with limited access, difficult terrain, or multiple structures may affect pricing."
              },
              {
                icon: CheckCircle,
                title: "Additional Services",
                description: "Pest inspections, electrical reports, or expedited delivery are available through our partners."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg border border-slate-200"
              >
                <item.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-chivo text-lg font-bold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add Ons */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
              Additional Services
            </h2>
            <p className="text-lg text-slate-600">
              Enhance your inspection with these optional services
            </p>
          </motion.div>

          <div className="bg-slate-50 p-8 rounded-lg border border-slate-200">
            <ul className="space-y-4">
              {addOns.map((addon, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">{addon}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-4xl font-bold mb-6">
            Ready to Book Your Inspection?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a detailed quote tailored to your property
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
            >
              <Link to="/contact">Request Quote</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold tracking-wide uppercase px-12"
            >
              <a href="tel:+61413245519">Call 0413 24 55 19</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
